import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { TemplatePagePartialModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/templatePagePartial/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/templatePagePartial/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/templatePagePartial/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/templatePagePartial/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/templatePagePartial/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/templatePagePartial/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/templatePagePartial/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/templatePagePartial/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/templatePagePartial/UPDATE_FAILURE'

export type TemplatePagePartialModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TemplatePagePartialModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type:UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTemplatePagePartials(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<TemplatePagePartialModel>> => {
    const loadedForKeys = [...getState().templatePagePartials.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/template_page_partials.json', options)
      .then(({ data }: { data: TemplatePagePartialModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_PARTIAL_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    // @ts-expect-error TS2322
    return promise
  }
}

export function createTemplatePagePartial(templatePagePartial: Partial<TemplatePagePartialModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_page_partial: templatePagePartial,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/template_page_partials.json', options, config)
      .then(({ data }: { data: TemplatePagePartialModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_PARTIAL)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateTemplatePagePartial(templatePagePartial: Partial<TemplatePagePartialModel>, options: EntityOptions = {}) {
  const { id } = templatePagePartial

  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_page_partial: templatePagePartial,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/template_page_partials/${id}/duplicate.json`, options, config)
      .then(({ data }: { data: TemplatePagePartialModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_PARTIAL)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// @ts-expect-error TS2304
export function toggleTemplatePagePartialVisibility(templatePagePartial: UpdateParams<TemplatePagePartialModel>) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/template_page_partials/${templatePagePartial.id}/toggle_visibility.json`, {}, config)
    .then(({ data }: { data: TemplatePagePartialModel }) => {
      const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_PARTIAL)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: TemplatePagePartialModuleState = initialState,
  action: Action = { type: '' },
): TemplatePagePartialModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
